import React from "react";
import { Link } from "react-router-dom";
import SEO from "../../../components/SEO";
import img31 from "../../../assets/img31.jpg";
import { CheckCircle } from "lucide-react";
import img10 from "../../../assets/img10.jpg";
import BlogSection from "../../../components/BlogSection";

export default function VisitorToCanadaInsuranceHero() {
  const container = "max-w-[1380px] mx-auto px-4 sm:px-6 md:px-10 lg:px-12";

  return (
    <>
      <SEO
        title="Assurance médicale d’urgence visiteurs Canada – einsured.ca"
        description="Obtenez une assurance médicale d’urgence pour visiteurs au Canada. Comparez les régimes et protégez votre santé durant votre séjour."
        keywords="Assurance visiteurs au Canada, assurance médicale d'urgence canada, assurance voyage pour visiteurs, soumissions assurance VTC"
      />
      {/* SECTION HÉRO */}
      <section
        className="relative min-h-[60vh] sm:min-h-[70vh] md:min-h-[80vh] bg-cover bg-center flex items-center justify-center md:justify-start"
        style={{ backgroundImage: `url(${img31})` }}
      >
        <div className="absolute inset-0 bg-black/40"></div>

        <div className={`relative z-10 w-full ${container}`}>
          <div className="max-w-3xl text-white text-center md:text-left px-2">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight mb-4">
              Assurance médicale
              <br />
              d&apos;urgence pour
              <br />
              visiteurs au Canada
            </h1>

            <p className="text-lg sm:text-xl font-medium mb-8">
              Votre clé pour voyager en toute confiance
            </p>

            <a
              href="https://book.einsured.ca/widget/bookings/ei-team"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-white font-semibold px-8 py-3 rounded-full transition shadow-md text-base sm:text-lg"
            >
              Commencer
            </a>
          </div>
        </div>
      </section>

      {/* SECTION INFO */}
      <section className="py-12 sm:py-16 md:py-20 bg-white">
        <div className={container}>
          <div className="max-w-5xl mx-auto text-gray-800">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#49628b] text-center mb-6">
              Visiteurs au Canada
            </h2>

            <p className="text-base leading-6 sm:text-lg sm:leading-8 mb-6">
              Lorsque vous voyagez au Canada en tant que{" "}
              <a
                href="https://www.canada.ca/en/revenue-agency/services/tax/international-non-residents/individuals-leaving-entering-canada-non-residents/non-residents-canada.html"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#20a7df] underline font-medium hover:opacity-90"
              >
                résident non canadien
              </a>
              , il est sage de considérer une assurance voyage spécialisée telle que l&apos;{" "}
              <span className="font-semibold text-[#20a7df]">
                Assurance médicale d&apos;urgence pour visiteurs au Canada.
              </span>{" "}
              Ce régime d&apos;assurance offre une protection financière pour les
              dépenses médicales imprévues pouvant survenir lors de votre visite.
              Grâce à cette assurance, vous pouvez profiter de votre séjour au
              Canada sans vous inquiéter des frais médicaux élevés.
            </p>

            <p className="text-base leading-6 sm:text-lg sm:leading-8 mb-6">
              L&apos;{" "}
              <span className="font-semibold text-[#20a7df]">
                Assurance médicale d&apos;urgence pour visiteurs au Canada
              </span>{" "}
              est adaptée aux besoins des visiteurs pendant leur séjour au
              Canada, leur donnant accès aux soins médicaux nécessaires. Elle
              est conçue pour faire face à tout problème de santé imprévu
              pouvant survenir lors de votre séjour, vous permettant de vous
              concentrer sur le plaisir de votre visite sans stress inutile.
            </p>

            <p className="text-base leading-6 sm:text-lg sm:leading-8 mb-6">
              Pour obtenir les meilleurs tarifs possibles pour le régime
              d&apos;assurance, il est recommandé de visiter l&apos;{" "}
              <span className="font-semibold text-[#20a7df]">
                outil de soumission VTC
              </span>{" "}
              sur einsured.ca. Ne laissez pas les dépenses médicales imprévues
              gâcher votre séjour au Canada ; assurez-vous d&apos;avoir la
              couverture appropriée avant d&apos;entreprendre votre voyage.
            </p>

            <p className="text-base leading-6 sm:text-lg sm:leading-8 mb-6">
              Pour éviter toute complication lors de leur visite, il est
              impératif que les visiteurs lisent attentivement les modalités et
              conditions de la police d&apos;assurance. Cela comprend la
              compréhension de toute clause relative aux{" "}
              <span className="font-semibold text-[#20a7df]">
                conditions préexistantes, exclusions et limitations.
              </span>
            </p>

            <p className="text-base leading-6 sm:text-lg sm:leading-8">
              Les visiteurs devraient opter pour une police d&apos;assurance qui
              répond à leurs besoins de santé, à la durée de leur séjour et aux
              activités prévues au Canada. Souscrire une assurance médicale
              d&apos;urgence pour visiteurs au Canada garantira que les visiteurs
              sont financièrement protégés en cas d&apos;urgences médicales
              inattendues lors de leur visite.
            </p>
          </div>
        </div>
      </section>

      {/* Pourquoi est-ce nécessaire */}
      <section
        className="relative py-20 sm:py-24 md:py-20 bg-cover bg-center lg:bg-fixed"
        style={{ backgroundImage: `url(${img10})` }}
      >
        <div className="absolute inset-0 bg-sky-900 bg-opacity-75"></div>

        <div className={`relative z-10 w-full ${container}`}>
          <div className="text-center text-white">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
              Pourquoi est-il nécessaire pour les voyageurs canadiens d&apos;avoir une
              assurance visiteurs au Canada ?
            </h2>
            <p className="text-lg sm:text-xl max-w-4xl mx-auto leading-relaxed mb-6">
              L&apos;{" "}
              <a
                href="/fr/devis/vtc"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sky-300 underline font-medium"
              >
                assurance visiteurs au Canada
              </a>{" "}
              est essentielle pour les voyageurs qui visitent le Canada pour
              diverses raisons. Bien que le Canada dispose d&apos;un système de
              santé bien établi, il ne fournit pas de soins médicaux gratuits
              aux non-résidents.
            </p>

            <h3 className="text-2xl sm:text-3xl font-semibold mb-12">
              Voici plusieurs raisons pour lesquelles les voyageurs ont besoin
              d&apos;une assurance visiteurs au Canada :
            </h3>

            {/* Grille de cartes 1 */}
            <div className="grid md:grid-cols-3 gap-6 mb-10">
              {/* Carte 1 */}
              <div className="bg-white text-gray-800 rounded-3xl p-6 sm:p-8 shadow-lg">
                <h4 className="text-xl font-bold text-[#49628b] mb-4">
                  Couverture des frais de santé :
                </h4>
                <p className="leading-relaxed">
                  Les frais médicaux au Canada peuvent être élevés et, sans
                  assurance, les voyageurs peuvent être responsables de couvrir
                  les coûts des services médicaux de leur poche. L&apos;assurance
                  visiteurs au Canada aide à atténuer le fardeau financier
                  associé aux urgences médicales inattendues.
                </p>
              </div>

              {/* Carte 2 */}
              <div className="bg-white text-gray-800 rounded-3xl p-6 sm:p-8 shadow-lg">
                <h4 className="text-xl font-bold text-[#49628b] mb-4">
                  Services médicaux d&apos;urgence :
                </h4>
                <p className="leading-relaxed">
                  L&apos;assurance visiteurs couvre généralement les services
                  médicaux d&apos;urgence, y compris l&apos;hospitalisation, la chirurgie,
                  les tests diagnostiques et les médicaments sur ordonnance.
                  Sans assurance, les voyageurs peuvent faire face à des
                  dépenses importantes pour les traitements médicaux d&apos;urgence.
                </p>
              </div>

              {/* Carte 3 */}
              <div className="bg-white text-gray-800 rounded-3xl p-6 sm:p-8 shadow-lg">
                <h4 className="text-xl font-bold text-[#49628b] mb-4">
                  Couverture de rapatriement :
                </h4>
                <p className="leading-relaxed">
                  Dans le malheureux cas d&apos;une maladie ou blessure grave,
                  l&apos;assurance visiteurs peut couvrir les frais de rapatriement,
                  garantissant que le voyageur peut retourner dans son pays
                  d&apos;origine pour un traitement médical supplémentaire si
                  nécessaire.
                </p>
              </div>
            </div>

            {/* Grille de cartes 2 */}
            <div className="grid md:grid-cols-3 gap-6 mb-10">
              {/* Carte 4 */}
              <div className="bg-white text-gray-800 rounded-3xl p-6 sm:p-8 shadow-lg">
                <h4 className="text-xl font-bold text-[#49628b] mb-4">
                  Protection contre les interruptions de voyage :
                </h4>
                <p className="leading-relaxed">
                  L&apos;assurance visiteurs peut offrir une couverture pour les
                  interruptions de voyage dues à des événements imprévus, comme
                  une urgence familiale ou une condition médicale obligeant le
                  voyageur à écourter sa visite au Canada.
                </p>
              </div>

              {/* Carte 5 */}
              <div className="bg-white text-gray-800 rounded-3xl p-6 sm:p-8 shadow-lg">
                <h4 className="text-xl font-bold text-[#49628b] mb-4">
                  Couverture décès et mutilation accidentels :
                </h4>
                <p className="leading-relaxed">
                  Certains régimes d&apos;assurance visiteurs offrent une couverture
                  pour le décès accidentel et la mutilation, offrant une
                  protection financière en cas d&apos;accident tragique.
                </p>
              </div>

              {/* Carte 6 */}
              <div className="bg-white text-gray-800 rounded-3xl p-6 sm:p-8 shadow-lg">
                <h4 className="text-xl font-bold text-[#49628b] mb-4">
                  Tranquillité d&apos;esprit pour les visiteurs et les hôtes :
                </h4>
                <p className="leading-relaxed">
                  L&apos;assurance visiteurs au Canada protège non seulement le
                  voyageur, mais offre également la tranquillité d&apos;esprit aux
                  hôtes ou aux membres de la famille qui pourraient être
                  financièrement responsables des frais médicaux engagés par le
                  visiteur.
                </p>
              </div>
            </div>

            {/* Grille de cartes 3 */}
            <div className="grid md:grid-cols-3 gap-6 mb-10">
              {/* Carte 7 */}
              <div className="bg-white text-gray-800 rounded-3xl p-6 sm:p-8 shadow-lg">
                <h4 className="text-xl font-bold text-[#49628b] mb-4">
                  Exigence obligatoire :
                </h4>
                <p className="leading-relaxed">
                  Dans certains cas, l&apos;assurance visiteurs peut être une
                  exigence obligatoire pour obtenir un visa ou entrer au Canada.
                  Il est important de vérifier les exigences d&apos;entrée
                  spécifiques et les réglementations pour le type de visa que
                  détient le voyageur.
                </p>
              </div>

              {/* Carte 8 */}
              <div className="bg-white text-gray-800 rounded-3xl p-6 sm:p-8 shadow-lg">
                <h4 className="text-xl font-bold text-[#49628b] mb-4">
                  Exigence légale dans certaines provinces :
                </h4>
                <p className="leading-relaxed">
                  Certaines provinces canadiennes peuvent légalement exiger que
                  les visiteurs disposent d&apos;une couverture d&apos;assurance maladie.
                  Par exemple, la province de Québec oblige les visiteurs à
                  avoir une assurance maladie pour la durée de leur séjour.
                </p>
              </div>

              {/* Carte 9 */}
              <div className="bg-white text-gray-800 rounded-3xl p-6 sm:p-8 shadow-lg">
                <h4 className="text-xl font-bold text-[#49628b] mb-4">
                  Protection contre les événements imprévus :
                </h4>
                <p className="leading-relaxed">
                  Les plans de voyage peuvent être perturbés par des événements
                  inattendus tels que des annulations de vols, des bagages
                  perdus ou d&apos;autres problèmes liés au voyage. Certaines
                  polices d&apos;assurance visiteurs offrent une couverture pour ces
                  urgences non médicales.
                </p>
              </div>
            </div>

            <p className="text-lg sm:text-xl max-w-5xl mx-auto leading-relaxed mb-10">
              Il est essentiel pour les{" "}
              <a
                href="https://www.canada.ca/en/immigration-refugees-citizenship/services/application/application-forms-guides/guide-5256-applying-visitor-visa-temporary-resident-visa.html"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sky-300 underline font-medium"
              >
                visiteurs au Canada
              </a>{" "}
              d&apos;examiner attentivement et de comprendre les modalités et les
              limites de couverture de leur{" "}
              <span className="font-semibold text-white">
                police d&apos;assurance visiteurs.
              </span>{" "}
              Choisir un régime d&apos;assurance approprié permet de s&apos;assurer que
              les voyageurs sont adéquatement protégés pendant leur séjour au
              Canada, offrant une sécurité financière et la tranquillité
              d&apos;esprit face aux événements imprévus.
            </p>

            {/* Bouton CTA */}
            <a
              href="/fr/devis/vtc"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-white font-semibold text-lg px-10 py-4 rounded-full shadow-md transition"
            >
              Obtenez votre meilleur tarif
            </a>
          </div>
        </div>
      </section>

      {/* SECTION TERMINOLOGIE */}
      <section className="py-16 sm:py-20 bg-white">
        <div className={container}>
          <div className="max-w-6xl mx-auto text-gray-800">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#49628b] text-center mb-6">
              Terminologie de l&apos;assurance médicale d&apos;urgence pour visiteurs au Canada
            </h2>
            <p className="text-base sm:text-lg leading-7 sm:leading-8 text-center max-w-4xl mx-auto mb-12">
              Comprendre la terminologie de base associée à l&apos;Assurance médicale
              d&apos;urgence pour visiteurs au Canada est essentiel pour les personnes
              souhaitant souscrire une couverture pour leur séjour au Canada.
              Voici quelques termes clés couramment utilisés dans ce contexte :
            </p>

            {/* GRILLE 1 */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {/* Frais médicaux d'urgence */}
              <div className="bg-[#f2f6fa] p-6 sm:p-8 rounded-3xl shadow">
                <h3 className="text-xl font-bold text-[#49628b] mb-4">
                  Frais médicaux d&apos;urgence :
                </h3>
                <p className="leading-relaxed flex gap-3">
                  <CheckCircle
                    className="h-6 w-6 text-sky-500 shrink-0 mt-1"
                    strokeWidth={2.5}
                  />
                  Coûts engagés pour un traitement médical nécessaire et
                  immédiat en raison d&apos;une maladie ou blessure inattendue.
                </p>
              </div>

              {/* Période de couverture */}
              <div className="bg-[#f2f6fa] p-6 sm:p-8 rounded-3xl shadow">
                <h3 className="text-xl font-bold text-[#49628b] mb-4">
                  Période de couverture :
                </h3>
                <p className="leading-relaxed flex gap-3">
                  <CheckCircle
                    className="h-6 w-6 text-sky-500 shrink-0 mt-1"
                    strokeWidth={2.5}
                  />
                  La durée pendant laquelle la police d&apos;assurance est en
                  vigueur, généralement spécifiée en jours ou en mois.
                </p>
              </div>
            </div>

            {/* GRILLE 2 */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {/* Prime */}
              <div className="bg-[#f2f6fa] p-6 sm:p-8 rounded-3xl shadow">
                <h3 className="text-xl font-bold text-[#49628b] mb-4">
                  Prime :
                </h3>
                <p className="leading-relaxed flex gap-3">
                  <CheckCircle
                    className="h-6 w-6 text-sky-500 shrink-0 mt-1"
                    strokeWidth={2.5}
                  />
                  Le montant payé par le titulaire de la police à la compagnie
                  d&apos;assurance pour la couverture. Les primes peuvent être payées
                  mensuellement, trimestriellement, semestriellement ou
                  annuellement.
                </p>
              </div>

              {/* Franchise */}
              <div className="bg-[#f2f6fa] p-6 sm:p-8 rounded-3xl shadow">
                <h3 className="text-xl font-bold text-[#49628b] mb-4">
                  Franchise :
                </h3>
                <p className="leading-relaxed flex gap-3">
                  <CheckCircle
                    className="h-6 w-6 text-sky-500 shrink-0 mt-1"
                    strokeWidth={2.5}
                  />
                  Le montant que l&apos;assuré doit payer de sa poche avant que la
                  couverture d&apos;assurance prenne effet. Par exemple, s&apos;il y a une
                  franchise de 500 $, l&apos;assuré doit payer les premiers 500 $
                  des dépenses couvertes avant que l&apos;assurance commence à couvrir
                  les frais.
                </p>
              </div>
            </div>

            {/* GRILLE 3 */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {/* Limite des prestations */}
              <div className="bg-[#f2f6fa] p-6 sm:p-8 rounded-3xl shadow">
                <h3 className="text-xl font-bold text-[#49628b] mb-4">
                  Limite des prestations :
                </h3>
                <p className="leading-relaxed flex gap-3">
                  <CheckCircle
                    className="h-6 w-6 text-sky-500 shrink-0 mt-1"
                    strokeWidth={2.5}
                  />
                  Le montant maximum que le fournisseur d&apos;assurance paiera pour
                  les frais médicaux couverts pendant la période de la police.
                  Cela peut inclure des sous-limites pour des types spécifiques
                  de services médicaux.
                </p>
              </div>

              {/* Condition préexistante */}
              <div className="bg-[#f2f6fa] p-6 sm:p-8 rounded-3xl shadow">
                <h3 className="text-xl font-bold text-[#49628b] mb-4">
                  Condition préexistante :
                </h3>
                <p className="leading-relaxed flex gap-3">
                  <CheckCircle
                    className="h-6 w-6 text-sky-500 shrink-0 mt-1"
                    strokeWidth={2.5}
                  />
                  Une condition médicale qui existait avant la date d&apos;entrée
                  en vigueur de la police d&apos;assurance. Certaines polices peuvent
                  avoir des exclusions ou des limitations liées aux conditions
                  préexistantes.
                </p>
              </div>
            </div>

            {/* GRILLE 4 */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {/* Exclusion */}
              <div className="bg-[#f2f6fa] p-6 sm:p-8 rounded-3xl shadow">
                <h3 className="text-xl font-bold text-[#49628b] mb-4">
                  Exclusion :
                </h3>
                <p className="leading-relaxed flex gap-3">
                  <CheckCircle
                    className="h-6 w-6 text-sky-500 shrink-0 mt-1"
                    strokeWidth={2.5}
                  />
                  Conditions ou circonstances spécifiques non couvertes par la
                  police d&apos;assurance. Il est important de connaître les
                  exclusions pour comprendre la portée de la couverture.
                </p>
              </div>

              {/* Rapatriement des dépouilles */}
              <div className="bg-[#f2f6fa] p-6 sm:p-8 rounded-3xl shadow">
                <h3 className="text-xl font-bold text-[#49628b] mb-4">
                  Rapatriement des dépouilles :
                </h3>
                <p className="leading-relaxed flex gap-3">
                  <CheckCircle
                    className="h-6 w-6 text-sky-500 shrink-0 mt-1"
                    strokeWidth={2.5}
                  />
                  Couverture pour les frais de rapatriement des dépouilles de
                  l&apos;assuré dans son pays d&apos;origine en cas de décès.
                </p>
              </div>
            </div>

            {/* GRILLE 5 */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {/* Services d'assistance d'urgence */}
              <div className="bg-[#f2f6fa] p-6 sm:p-8 rounded-3xl shadow">
                <h3 className="text-xl font-bold text-[#49628b] mb-4">
                  Services d&apos;assistance d&apos;urgence :
                </h3>
                <p className="leading-relaxed flex gap-3">
                  <CheckCircle
                    className="h-6 w-6 text-sky-500 shrink-0 mt-1"
                    strokeWidth={2.5}
                  />
                  Services de soutien 24/7, souvent fournis par une société
                  d&apos;assistance, offrant des conseils, une coordination et une
                  aide lors d&apos;urgences médicales.
                </p>
              </div>

              {/* Décès et mutilation accidentels */}
              <div className="bg-[#f2f6fa] p-6 sm:p-8 rounded-3xl shadow">
                <h3 className="text-xl font-bold text-[#49628b] mb-4">
                  Décès et mutilation accidentels (DMA) :
                </h3>
                <p className="leading-relaxed flex gap-3">
                  <CheckCircle
                    className="h-6 w-6 text-sky-500 shrink-0 mt-1"
                    strokeWidth={2.5}
                  />
                  Couverture pour un montant de prestation spécifique en cas de
                  décès accidentel ou de perte de membres ou de la vue
                  résultant d&apos;un accident.
                </p>
              </div>
            </div>

            {/* GRILLE 6 */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {/* Interruption de voyage */}
              <div className="bg-[#f2f6fa] p-6 sm:p-8 rounded-3xl shadow">
                <h3 className="text-xl font-bold text-[#49628b] mb-4">
                  Interruption de voyage :
                </h3>
                <p className="leading-relaxed flex gap-3">
                  <CheckCircle
                    className="h-6 w-6 text-sky-500 shrink-0 mt-1"
                    strokeWidth={2.5}
                  />
                  Couverture pour les dépenses engagées en raison d&apos;interruptions
                  inattendues dans les plans de voyage, comme le retour
                  prématuré à domicile en raison d&apos;une urgence médicale.
                </p>
              </div>

              {/* Assureur */}
              <div className="bg-[#f2f6fa] p-6 sm:p-8 rounded-3xl shadow">
                <h3 className="text-xl font-bold text-[#49628b] mb-4">
                  Assureur :
                </h3>
                <p className="leading-relaxed flex gap-3">
                  <CheckCircle
                    className="h-6 w-6 text-sky-500 shrink-0 mt-1"
                    strokeWidth={2.5}
                  />
                  La compagnie d&apos;assurance ou l&apos;entité qui évalue les risques et
                  détermine les modalités et conditions de la couverture.
                </p>
              </div>
            </div>

            {/* GRILLE 7 */}
            <div className="grid md:grid-cols-2 gap-6 mb-10">
              {/* Titulaire de la police */}
              <div className="bg-[#f2f6fa] p-6 sm:p-8 rounded-3xl shadow">
                <h3 className="text-xl font-bold text-[#49628b] mb-4">
                  Titulaire de la police :
                </h3>
                <p className="leading-relaxed flex gap-3">
                  <CheckCircle
                    className="h-6 w-6 text-sky-500 shrink-0 mt-1"
                    strokeWidth={2.5}
                  />
                  La personne qui possède et détient la police d&apos;assurance.
                </p>
              </div>

              {/* Réclamation */}
              <div className="bg-[#f2f6fa] p-6 sm:p-8 rounded-3xl shadow">
                <h3 className="text-xl font-bold text-[#49628b] mb-4">
                  Réclamation :
                </h3>
                <p className="leading-relaxed flex gap-3">
                  <CheckCircle
                    className="h-6 w-6 text-sky-500 shrink-0 mt-1"
                    strokeWidth={2.5}
                  />
                  Une demande faite par le titulaire de la police à la
                  compagnie d&apos;assurance pour le remboursement des dépenses
                  couvertes.
                </p>
              </div>
            </div>
            <div className="bg-[#f2f6fa] p-6 sm:p-8 rounded-3xl shadow mb-10">
              <h3 className="text-xl font-bold text-[#49628b] mb-4">
                Assuré :
              </h3>
              <p className="leading-relaxed flex gap-3">
                <CheckCircle
                  className="h-6 w-6 text-sky-500 shrink-0 mt-1"
                  strokeWidth={2.5}
                />
                La personne couverte par la police d&apos;assurance.
              </p>
            </div>
            <p className="text-base sm:text-lg leading-7 sm:leading-8 max-w-4xl mx-auto mb-6 text-center">
              Comprendre ces termes est essentiel pour que les personnes puissent
              prendre des décisions éclairées lors du choix d&apos;une police
              d&apos;Assurance médicale d&apos;urgence pour visiteurs au Canada.
            </p>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 max-w-4xl mx-auto mb-10 text-center">
              Il est recommandé de lire attentivement les documents de la police
              et de consulter le fournisseur d&apos;assurance ou un conseiller en
              assurance en cas de questions ou d&apos;incertitudes concernant la
              couverture et les modalités.
            </p>

            {/* BOUTON CTA */}
            <div className="text-center">
              <a
                href="/fr/devis/vtc"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-white font-semibold text-lg px-10 py-4 rounded-full shadow-md transition"
              >
                Obtenez votre meilleur tarif
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* OPTIONS DE PAIEMENT FLEXIBLES */}
      <section
        className="relative py-20 sm:py-20 md:py-26"
        style={{
          background: "linear-gradient(135deg, #6d86a6 0%, #61add1 100%)",
        }}
      >
        <div className={`relative z-10 w-full ${container}`}>
          <div className="text-white">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-8">
              Options de paiement flexibles
            </h2>
            <p className="text-lg sm:text-xl leading-relaxed max-w-5xl mx-auto text-center mb-12">
              Les options de paiement pour les polices d&apos;{" "}
              <span className="font-semibold text-[#20a7df]">
                Assurance médicale d&apos;urgence pour visiteurs au Canada
              </span>{" "}
              peuvent varier selon les assureurs. Alors que certaines compagnies
              offrent de la flexibilité dans les échéanciers de paiement des
              primes, d&apos;autres peuvent avoir des structures de paiement plus
              rigides. Voici les options de paiement courantes, incluant celles
              avec possibilité de versements mensuels :
            </p>
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* Primes annuelles */}
              <div className="bg-[#4a6a8a] bg-opacity-70 text-white p-6 sm:p-8 lg:p-10 rounded-3xl shadow-lg">
                <h3 className="text-2xl font-bold text-center mb-6">
                  Primes annuelles :
                </h3>
                <p className="leading-relaxed flex gap-3">
                  <CheckCircle
                    className="h-6 w-6 text-sky-300 shrink-0 mt-1"
                    strokeWidth={2.5}
                  />
                  Le titulaire de la police paie la totalité de la prime pour
                  la période de couverture à l&apos;avance sur une base annuelle.
                  Il s&apos;agit d&apos;une option de paiement standard.
                </p>
              </div>

              {/* Primes mensuelles */}
              <div className="bg-[#4a6a8a] bg-opacity-70 text-white p-6 sm:p-8 lg:p-10 rounded-3xl shadow-lg">
                <h3 className="text-2xl font-bold text-center mb-6">
                  Primes mensuelles :
                </h3>

                <p className="leading-relaxed flex gap-3 mb-4">
                  <CheckCircle
                    className="h-6 w-6 text-sky-300 shrink-0 mt-1"
                    strokeWidth={2.5}
                  />
                  <span>
                    Les{" "}
                    <Link to="/fr/devis/vtc" className="text-sky-300 underline">
                      paiements mensuels de prime
                    </Link>{" "}
                    permettent aux titulaires de police d&apos;étaler le coût de
                    l&apos;assurance sur la durée de la période de couverture.
                    Cependant, tous les assureurs n&apos;offrent pas d&apos;options de
                    versements mensuels.
                  </span>
                </p>

                <p className="leading-relaxed flex gap-3">
                  <CheckCircle
                    className="h-6 w-6 text-sky-300 shrink-0 mt-1"
                    strokeWidth={2.5}
                  />
                  La disponibilité des options de versements mensuels dépend
                  de la compagnie d&apos;assurance et de la police spécifique.
                </p>
              </div>
            </div>
            <h3 className="text-2xl sm:text-3xl font-semibold text-center mb-10">
              Voici quelques facteurs pouvant influencer si les paiements
              mensuels sont offerts :
            </h3>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              {/* Type de police */}
              <div className="bg-[#4a6a8a] bg-opacity-70 text-white p-6 sm:p-8 rounded-3xl shadow-lg">
                <h4 className="text-xl font-bold mb-4 text-center">
                  Type de police :
                </h4>
                <p className="leading-relaxed flex gap-3">
                  <CheckCircle
                    className="h-6 w-6 text-sky-300 shrink-0 mt-1"
                    strokeWidth={2.5}
                  />
                  Certains assureurs peuvent offrir des options de paiement
                  mensuel pour des types spécifiques de polices, comme les
                  régimes complets ou les limites de couverture plus élevées.
                </p>
              </div>

              {/* Montant de la prime */}
              <div className="bg-[#4a6a8a] bg-opacity-70 text-white p-6 sm:p-8 rounded-3xl shadow-lg">
                <h4 className="text-xl font-bold mb-4 text-center">
                  Montant de la prime :
                </h4>
                <p className="leading-relaxed flex gap-3">
                  <CheckCircle
                    className="h-6 w-6 text-sky-300 shrink-0 mt-1"
                    strokeWidth={2.5}
                  />
                  Les paiements mensuels peuvent être plus courants pour les
                  polices à primes plus élevées. Les fournisseurs peuvent être
                  plus enclins à offrir des plans de versements pour les
                  paiements de primes plus importants.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* Durée de la couverture */}
              <div className="bg-[#4a6a8a] bg-opacity-70 text-white p-6 sm:p-8 rounded-3xl shadow-lg">
                <h4 className="text-xl font-bold mb-4 text-center">
                  Durée de la couverture :
                </h4>
                <p className="leading-relaxed flex gap-3">
                  <CheckCircle
                    className="h-6 w-6 text-sky-300 shrink-0 mt-1"
                    strokeWidth={2.5}
                  />
                  Les paiements mensuels peuvent être plus courants pour les
                  polices à long terme, où la prime annuelle totale est divisée
                  en montants mensuels plus petits.
                </p>
              </div>

              {/* Mode de paiement */}
              <div className="bg-[#4a6a8a] bg-opacity-70 text-white p-6 sm:p-8 rounded-3xl shadow-lg">
                <h4 className="text-xl font-bold mb-4 text-center">
                  Mode de paiement :
                </h4>
                <p className="leading-relaxed flex gap-3">
                  <CheckCircle
                    className="h-6 w-6 text-sky-300 shrink-0 mt-1"
                    strokeWidth={2.5}
                  />
                  Certains assureurs peuvent exiger des prélèvements bancaires
                  automatiques ou des paiements par carte de crédit pour les
                  versements mensuels.
                </p>
              </div>
            </div>

            <p className="text-lg sm:text-xl leading-relaxed max-w-5xl mx-auto mb-6 text-center">
              Il est essentiel que les personnes vérifient auprès des experts
              financiers d&apos;{" "}
              <a
                href="/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-sky-300 underline"
              >
                einsured.ca
              </a>{" "}
              leurs options de paiement, notamment si des versements mensuels
              sont disponibles et dans quelles conditions. De plus, les
              titulaires de police doivent être conscients de tout frais
              supplémentaire associé aux différentes fréquences de paiement.
            </p>

            <p className="text-lg sm:text-xl leading-relaxed max-w-5xl mx-auto mb-12 text-center">
              Lors du choix d&apos;une police d&apos;Assurance médicale d&apos;urgence pour
              visiteurs au Canada, les personnes doivent tenir compte de leur
              budget, du coût total de la prime et de la commodité des
              différentes options de paiement.
            </p>

            {/* BOUTON CTA */}
            <div className="text-center">
              <a
                href="https://book.einsured.ca/widget/bookings/ei-team"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-white font-semibold text-lg px-10 py-4 rounded-full shadow-md transition"
              >
                Obtenir la meilleure option
              </a>
            </div>
          </div>
        </div>
      </section>

      <BlogSection title="Derniers blogues" mode="related" relatedCount={3} />
    </>
  );
}
